import { useState } from 'react';
import {
  SUBSCRIBABLE_PRODUCT_KEYS,
  portalInvitationCreateSchema,
  type AdminCustomerAccount,
  type AdminPortal,
  type PortalRole,
  type SubscribableProductKey,
} from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { RadioGroup, ReasonField, SelectField, TextField, reasonError } from '../../components/fields';
import { DateText, ScrollTable, StatusBadge } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { productName } from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { useOrganizationScope, useSupportAccessHint } from './scope';

const LIVE = new Set(['trialing', 'active', 'past_due']);

const PORTAL_STATUS_CHOICES = [
  { value: 'active', label: 'Active', description: "The organization's customers sign in to it." },
  {
    value: 'disabled',
    label: 'Disabled',
    description: 'Closed: nobody signs in to it until it is enabled again.',
  },
] as const;

const ACCOUNT_STATUS_CHOICES = [
  { value: 'active', label: 'Active', description: 'Its people use the portals.' },
  { value: 'suspended', label: 'Suspended', description: 'Its people lose portal access until reactivated.' },
] as const;

const PORTAL_ROLE_CHOICES = [
  { value: 'portal_user', label: 'Portal user', description: "Uses the portal for the customer's shipments." },
  {
    value: 'portal_admin',
    label: 'Portal admin',
    description: "Also manages the customer's other people in the portal.",
  },
] as const;

type Dialog =
  | { kind: 'new-portal' }
  | { kind: 'portal'; portal: AdminPortal }
  | { kind: 'new-account' }
  | { kind: 'account'; account: AdminCustomerAccount }
  | { kind: 'invite'; account: AdminCustomerAccount };

interface Form {
  productKey: string;
  displayName: string;
  externalRef: string;
  status: string;
  reason: string;
  portalId: string;
  email: string;
  role: string;
}

type Errors = Partial<Record<'productKey' | 'displayName' | 'reason' | 'portalId' | 'email', string>>;

const EMPTY: Form = {
  productKey: '',
  displayName: '',
  externalRef: '',
  status: 'active',
  reason: '',
  portalId: '',
  email: '',
  role: 'portal_user',
};

function CopyId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="admin-id-copy">
      <code className="admin-id">{id}</code>{' '}
      <button
        type="button"
        className="admin-action"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(id);
            setCopied(true);
          } catch {
            setCopied(false);
          }
        }}
        aria-label={`Copy the portal ID ${id}`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

/**
 * An organization's customer portals and the customer accounts that use them. A portal's ID is what trace-portal's
 * PORTAL_IDS names, so it is shown in full with a copy button. A portal opens only while it is active and the
 * organization subscribes to its product, so a portal of a product without a live subscription says so. Platform admins
 * create and change both (staff.orgs.manage); every staff role reads them. Staff who manage members
 * (staff.members.manage) invite a person of an active customer account to an active portal: accepting makes them a
 * member of that portal for that account. platform-api decides again on every request.
 */
export function PortalsTab() {
  const { organization, abilities } = useOrganizationScope();
  const hint = useSupportAccessHint();
  const { api } = useAdmin();
  const organizationId = organization.id;
  const portalsKey = `portals\u0000${organizationId}`;
  const accountsKey = `customer-accounts\u0000${organizationId}`;
  const portalsRead = useAdminRead(portalsKey, (signal) => api.listPortals(organizationId, signal));
  const accountsRead = useAdminRead(accountsKey, (signal) =>
    api.listCustomerAccounts(organizationId, signal),
  );
  const portals =
    portalsRead.dataKey === portalsKey && !portalsRead.failure ? (portalsRead.data?.items ?? null) : null;
  const accounts =
    accountsRead.dataKey === accountsKey && !accountsRead.failure ? (accountsRead.data?.items ?? null) : null;
  const activePortals = (portals ?? []).filter((portal) => portal.status === 'active');
  const accountActions = abilities.managePortals || abilities.manageMembers;
  const subscribed = new Set(
    organization.products.filter((p) => LIVE.has(p.status)).map((p) => p.productKey),
  );

  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<Dialog>();
  const [form, setForm] = useState<Form>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const set = <K extends keyof Form>(field: K, value: Form[K]) =>
    setForm((current) => ({ ...current, [field]: value }));
  const open = (target: Dialog) => {
    if (target.kind === 'portal') {
      setForm({ ...EMPTY, displayName: target.portal.displayName, status: target.portal.status });
    } else if (target.kind === 'account') {
      setForm({
        ...EMPTY,
        displayName: target.account.displayName,
        externalRef: target.account.externalRef ?? '',
        status: target.account.status,
      });
    } else if (target.kind === 'invite') {
      // One active portal is the usual case: chosen for them.
      setForm({ ...EMPTY, portalId: activePortals.length === 1 ? activePortals[0]!.id : '' });
    } else {
      setForm(EMPTY);
    }
    setErrors({});
    dialog.show(target);
  };
  const target = dialog.target;
  const isPortal = target?.kind === 'new-portal' || target?.kind === 'portal';

  const titles: Record<Dialog['kind'], string> = {
    'new-portal': 'Create a portal',
    portal: target?.kind === 'portal' ? `Change ${target.portal.displayName}` : '',
    'new-account': 'Add a customer account',
    account: target?.kind === 'account' ? `Change ${target.account.displayName}` : '',
    invite: target?.kind === 'invite' ? `Invite a person of ${target.account.displayName}` : '',
  };

  return (
    <>
      <SavedNotice message={notice} />
      <PageSection
        title="Customer portals"
        description={
          <>
            Websites this organization gives its own customers (for example the shippers it moves freight for) to see
            their quotes, shipments and documents. Each portal belongs to one of the organization&apos;s products,
            shown in the Product column. This organization&apos;s own access to Trace products is under Overview,
            Products, not here. To serve a portal, put its ID in the portal app&apos;s PORTAL_IDS.
          </>
        }
        action={
          abilities.managePortals ? (
            <button
              type="button"
              className="admin-action admin-action--primary"
              onClick={() => open({ kind: 'new-portal' })}
            >
              Create a portal
            </button>
          ) : undefined
        }
      >
        {portalsRead.failure ? (
          <AdminProblem
            failure={portalsRead.failure}
            subject="the portals"
            onRetry={portalsRead.retry}
            supportAccess={hint}
          />
        ) : !portals ? (
          <LoadingBlock label="Loading the portals" />
        ) : portals.length === 0 ? (
          <EmptyPageState
            icon={<NavigationGlyph name="Users" />}
            title="No portals yet"
            description="This organization provides no customer portal."
          />
        ) : (
          <ScrollTable caption="Portals and their IDs" className="admin-table" busy={portalsRead.loading}>
            <thead>
              <tr>
                <th scope="col">Portal</th>
                <th scope="col">Product</th>
                <th scope="col">Status</th>
                <th scope="col">Portal ID</th>
                {abilities.managePortals ? <th scope="col">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {portals.map((portal) => (
                <tr key={portal.id}>
                  <th scope="row">{portal.displayName}</th>
                  <td>
                    {productName(portal.productKey)}
                    {subscribed.has(portal.productKey) ? null : (
                      <div className="admin-muted">Not subscribed: the portal stays closed until it is.</div>
                    )}
                  </td>
                  <td>
                    <StatusBadge status={portal.status} />
                  </td>
                  <td>
                    <CopyId id={portal.id} />
                  </td>
                  {abilities.managePortals ? (
                    <td>
                      <button
                        type="button"
                        className="admin-action"
                        onClick={() => open({ kind: 'portal', portal })}
                      >
                        Change
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        )}
      </PageSection>
      <PageSection
        title="Customer accounts"
        description="The companies that use this organization's portals. Invite their people to a portal here, or from the portal itself."
        action={
          abilities.managePortals ? (
            <button type="button" className="admin-action" onClick={() => open({ kind: 'new-account' })}>
              Add a customer account
            </button>
          ) : undefined
        }
      >
        {accountsRead.failure ? (
          <AdminProblem
            failure={accountsRead.failure}
            subject="the customer accounts"
            onRetry={accountsRead.retry}
            supportAccess={hint}
          />
        ) : !accounts ? (
          <LoadingBlock label="Loading the customer accounts" />
        ) : accounts.length === 0 ? (
          <p className="admin-muted">No customer accounts yet.</p>
        ) : (
          <ScrollTable
            caption="Customer accounts and their references"
            className="admin-table"
            busy={accountsRead.loading}
          >
            <thead>
              <tr>
                <th scope="col">Customer</th>
                <th scope="col">Reference</th>
                <th scope="col">Status</th>
                <th scope="col">Added</th>
                {accountActions ? <th scope="col">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <th scope="row">{account.displayName}</th>
                  <td>{account.externalRef ?? <span className="admin-muted">None</span>}</td>
                  <td>
                    <StatusBadge status={account.status} />
                  </td>
                  <td>
                    <DateText iso={account.createdAt} />
                  </td>
                  {accountActions ? (
                    <td>
                      {abilities.managePortals ? (
                        <button
                          type="button"
                          className="admin-action"
                          onClick={() => open({ kind: 'account', account })}
                        >
                          Change
                        </button>
                      ) : null}{' '}
                      {abilities.manageMembers && account.status === 'active' && activePortals.length > 0 ? (
                        <button
                          type="button"
                          className="admin-action"
                          onClick={() => open({ kind: 'invite', account })}
                          aria-label={`Invite a person of ${account.displayName}`}
                        >
                          Invite a person
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        )}
      </PageSection>
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={target ? titles[target.kind] : ''}
        description={
          isPortal
            ? `A customer portal of ${organization.displayName}.`
            : target?.kind === 'invite'
              ? `They get an email with a link; signing in with it makes them a member of the portal for ${target.account.displayName}.`
              : `A company that uses ${organization.displayName}'s portals.`
        }
        submitLabel={
          target?.kind === 'invite'
            ? 'Send the invitation'
            : target?.kind === 'new-portal' || target?.kind === 'new-account'
              ? 'Create'
              : 'Save the change'
        }
        onSubmit={async () => {
          const found: Errors = {};
          if (target?.kind === 'invite') {
            const email = form.email.trim();
            if (!form.portalId) found.portalId = 'Choose a portal.';
            if (!email) found.email = 'Enter an email address.';
            else if (!portalInvitationCreateSchema.shape.email.safeParse(email).success)
              found.email = 'Enter a valid email address.';
            setErrors(found);
            if (Object.keys(found).length > 0) return 'invalid';
            await api.createPortalInvitation(organizationId, form.portalId, {
              customerAccountId: target.account.id,
              email,
              role: form.role as PortalRole,
            });
            return undefined;
          }
          if (target?.kind === 'new-portal' && !form.productKey) found.productKey = 'Choose a product.';
          if (!form.displayName.trim()) found.displayName = 'Enter a name.';
          const reason = reasonError(form.reason);
          if (reason) found.reason = reason;
          setErrors(found);
          if (Object.keys(found).length > 0) return 'invalid';
          const displayName = form.displayName.trim();
          const externalRef = form.externalRef.trim() || null;
          if (target?.kind === 'new-portal') {
            await api.createPortal(organizationId, {
              productKey: form.productKey as SubscribableProductKey,
              displayName,
              reason: form.reason,
            });
          } else if (target?.kind === 'portal') {
            await api.changePortal(organizationId, target.portal.id, {
              displayName,
              status: form.status as AdminPortal['status'],
              reason: form.reason,
            });
          } else if (target?.kind === 'new-account') {
            await api.createCustomerAccount(organizationId, {
              displayName,
              externalRef,
              reason: form.reason,
            });
          } else if (target?.kind === 'account') {
            await api.changeCustomerAccount(organizationId, target.account.id, {
              displayName,
              externalRef,
              status: form.status as AdminCustomerAccount['status'],
              reason: form.reason,
            });
          }
          return undefined;
        }}
        onSaved={() => {
          if (target?.kind === 'invite') {
            setNotice(`Invitation sent to ${form.email.trim()}.`);
            return;
          }
          setNotice(`${form.displayName.trim()}: saved.`);
          if (isPortal) portalsRead.retry();
          else accountsRead.retry();
        }}
      >
        {target?.kind === 'new-portal' ? (
          <SelectField
            label="Product"
            value={form.productKey}
            onChange={(value) => set('productKey', value)}
            options={[
              { value: '', label: 'Choose a product' },
              ...SUBSCRIBABLE_PRODUCT_KEYS.map((k) => ({
                value: k,
                label: subscribed.has(k) ? productName(k) : `${productName(k)} (not subscribed)`,
              })),
            ]}
            hint="The product the portal belongs to. It opens only while the organization subscribes to it."
            error={errors.productKey ?? null}
            required
          />
        ) : null}
        {target?.kind === 'invite' ? (
          <>
            <SelectField
              label="Portal"
              value={form.portalId}
              onChange={(value) => set('portalId', value)}
              options={[
                { value: '', label: 'Choose a portal' },
                ...activePortals.map((portal) => ({ value: portal.id, label: portal.displayName })),
              ]}
              error={errors.portalId ?? null}
              required
            />
            <TextField
              label="Email address"
              type="email"
              value={form.email}
              onChange={(value) => set('email', value)}
              error={errors.email ?? null}
              spellCheck={false}
              required
            />
            <RadioGroup
              legend="Role"
              choices={PORTAL_ROLE_CHOICES}
              value={form.role}
              onChange={(value) => set('role', value)}
            />
          </>
        ) : (
          <TextField
            label="Name"
            value={form.displayName}
            onChange={(value) => set('displayName', value)}
            error={errors.displayName ?? null}
            required
          />
        )}
        {target?.kind === 'new-account' || target?.kind === 'account' ? (
          <TextField
            label="Reference"
            value={form.externalRef}
            onChange={(value) => set('externalRef', value)}
            hint="Needed for the Customer Portal: the customer's code in Trace for Brokers, whose quotes and shipments the portal shows. Unique."
            spellCheck={false}
          />
        ) : null}
        {target?.kind === 'portal' ? (
          <RadioGroup
            legend="Status"
            choices={PORTAL_STATUS_CHOICES}
            value={form.status}
            onChange={(value) => set('status', value)}
          />
        ) : null}
        {target?.kind === 'account' ? (
          <RadioGroup
            legend="Status"
            choices={ACCOUNT_STATUS_CHOICES}
            value={form.status}
            onChange={(value) => set('status', value)}
          />
        ) : null}
        {target?.kind === 'invite' ? null : (
          <ReasonField
            value={form.reason}
            onChange={(value) => set('reason', value)}
            error={errors.reason ?? null}
          />
        )}
      </WriteDialog>
    </>
  );
}
